const DEFAULT_THESIS =
  "Current deal evidence supports a value-add acquisition route, but the investment case still depends on validating income, planning constraints, and execution cost before committing to the strategy."

const DEFAULT_SUMMARY =
  "Concept Engine has formed an initial commercial judgement from the current deal record. Review the thesis, choose the strongest route, then generate execution tasks before appraisal."

export function buildConceptContract({ deal, strategies, selectedStrategy, latestRun, conceptEngine }) {
  if (conceptEngine?.thesis) {
    return buildPersistedConceptContract({
      conceptEngine,
      fallbackStrategies: strategies,
      selectedStrategy,
    })
  }

  const strategyRows = strategies.map(normalizeStrategy)
  const activeStrategy = strategyRows.find((strategy) => (
    strategy.id === selectedStrategy || strategy.persistedId === selectedStrategy
  )) ?? null
  const evidenceValues = latestRun?.evidenceValues ?? {}
  const availableEvidence = arrayValue(latestRun?.availableEvidence)
  const missingEvidence = arrayValue(latestRun?.missingEvidence)
  const confidenceScore = scoreAverage(strategyRows.map((strategy) => strategy.confidenceScore))
  const valueEnhancements = valueEnhancementsFor(activeStrategy)

  return {
    thesis: {
      status: strategyRows.length > 0 ? "generated" : "draft",
      commercialJudgement: commercialJudgementFor(strategyRows, deal),
      thesisText: thesisTextFor(strategyRows, deal),
      summary: DEFAULT_SUMMARY,
      confidence: confidenceLabel(confidenceScore),
      confidenceScore,
      primaryValueSource: primaryValueSourceFor(strategyRows),
      valueDrivers: valueDriversFor(strategyRows),
      opportunityClassification: opportunityClassificationFor(strategyRows, evidenceValues),
      evidenceUsed: availableEvidence,
      missingEvidence,
      generatedAt: latestRun?.completedAt ?? null,
      evidenceVersion: evidenceVersionFor(latestRun),
      displayStatus: "Current",
    },
    strategies: strategyRows,
    selectedStrategy: activeStrategy,
    valueEnhancements,
    hasGeneratedValueEnhancements: valueEnhancements.length > 0,
    reviewState: {
      isOutOfDate: false,
      changedEvidence: [],
      reviewThesisAvailable: true,
      continuePreviousJudgementAvailable: true,
    },
  }
}

function buildPersistedConceptContract({ conceptEngine, fallbackStrategies, selectedStrategy }) {
  const persistedStrategies = arrayValue(conceptEngine.strategies)
  const strategyRows = (persistedStrategies.length > 0 ? persistedStrategies : fallbackStrategies).map(normalizeStrategy)
  const currentSelection = strategyRows.find((strategy) => (
    strategy.id === selectedStrategy || strategy.persistedId === selectedStrategy
  ))
  const selected = currentSelection ?? conceptEngine.selectedStrategy
  const generatedValueEnhancements = arrayValue(conceptEngine.valueEnhancements).map(normalizeValueEnhancement)

  return {
    thesis: {
      ...conceptEngine.thesis,
      confidence: conceptEngine.thesis.confidence ?? confidenceLabel(conceptEngine.thesis.confidenceScore),
      valueDrivers: arrayValue(conceptEngine.thesis.valueDrivers).map(normalizeValueDriver),
      opportunityClassification: arrayValue(conceptEngine.thesis.opportunityClassification).map(formatLabel),
      evidenceUsed: arrayValue(conceptEngine.thesis.evidenceUsed),
      missingEvidence: arrayValue(conceptEngine.thesis.missingEvidence),
      evidenceVersion: conceptEngine.evidenceVersion?.version ?? conceptEngine.evidenceVersion?.id ?? null,
      displayStatus: conceptEngine.thesis.displayStatus ?? "Current",
    },
    strategies: strategyRows,
    selectedStrategy: selected ? normalizeStrategy(selected) : null,
    valueEnhancements: generatedValueEnhancements.filter((item) => item.actionState !== "dismissed"),
    hasGeneratedValueEnhancements: generatedValueEnhancements.length > 0,
    reviewState: {
      isOutOfDate: Boolean(conceptEngine.reviewState?.isOutOfDate),
      changedEvidence: arrayValue(conceptEngine.reviewState?.changedEvidence),
      reviewThesisAvailable: conceptEngine.reviewState?.reviewThesisAvailable ?? true,
      continuePreviousJudgementAvailable: conceptEngine.reviewState?.continuePreviousJudgementAvailable ?? true,
    },
  }
}

function normalizeValueEnhancement(item, index) {
  return {
    ...item,
    id: item.id ?? `enhancement-${index + 1}`,
    impact: compactMetricLabel(item.impact),
    cost: compactMetricLabel(item.cost),
    complexity: compactMetricLabel(item.complexity),
    confidence: formatPercent(item.confidenceScore ?? item.confidence),
    valueUplift: compactMetricLabel(item.valueUplift ?? item.expectedValueUplift),
  }
}

function normalizeStrategy(strategy) {
  const metrics = strategy.metrics ?? {}

  return {
    ...strategy,
    purpose: strategy.purpose ?? strategy.summary ?? strategy.headline ?? strategy.name,
    confidenceScore: strategy.confidenceScore ?? strategy.metrics?.confidenceScore ?? null,
    judgementScore: strategy.judgementScore ?? strategy.metrics?.judgementScore ?? null,
    metrics: {
      ...metrics,
      valuePotential: compactPotentialLabel(metrics.valuePotential),
      incomePotential: compactIncomeLabel(metrics.incomePotential),
      complexity: compactMetricLabel(metrics.complexity),
      timeToExecute: compactTimelineLabel(metrics.timeToExecute),
    },
    developmentAngle: {
      ...strategy.developmentAngle,
      works: normalizeWorksType(strategy.developmentAngle?.works),
      planning: normalizePlanningNotes(strategy.developmentAngle?.planning),
    },
    nextValidationTasks: nextValidationTasksFor(strategy),
    rejectedReason: rejectedReasonFor(strategy),
  }
}

function commercialJudgementFor(strategies, deal) {
  const strongest = strategies[0]
  if (!strongest) return "Investment judgement pending"

  const source = primaryValueSourceFor(strategies)
  return `${deal.address} appears to be a ${source.toLowerCase()} opportunity, led by ${strongest.name}.`
}

function thesisTextFor(strategies, deal) {
  const strongest = strategies[0]
  if (!strongest) return DEFAULT_THESIS

  const reasons = arrayValue(strongest.whyThisWorks)
  const firstReason = firstPresent(reasons) ?? firstPresent(strongest.valueAddLogic)
  const risk = firstPresent(strongest.risks)

  return [
    `${deal.address} currently points toward ${strongest.name} as the strongest commercial route.`,
    firstReason ? `${firstReason}.` : "The available evidence supports further strategy validation.",
    risk ? `The main constraint to validate is ${risk.toLowerCase()}.` : "No major concept constraint is currently flagged by current evidence.",
  ].join(" ")
}

function primaryValueSourceFor(strategies) {
  const strongest = strategies[0]
  if (!strongest) return "Commercial judgement"

  return strongest.metrics?.incomePotential || strongest.strategyType || strongest.name
}

function opportunityClassificationFor(strategies, evidenceValues) {
  const labels = [
    evidenceValues.asset_type_label || evidenceValues.asset_type || "Value-add",
    strategies[0]?.metrics?.valuePotential ? `${strategies[0].metrics.valuePotential} value potential` : null,
    strategies[0]?.metrics?.complexity ? `${strategies[0].metrics.complexity} complexity` : null,
  ].filter(Boolean)

  return labels.slice(0, 4)
}

function valueDriversFor(strategies) {
  const explicitDrivers = arrayValue(strategies[0]?.valueDrivers)
  if (explicitDrivers.length > 0) return explicitDrivers.slice(0, 4).map(normalizeValueDriver)

  return strategies.slice(0, 4).map((strategy) => ({
    id: strategy.id,
    title: strategy.name,
    description: strategy.purpose,
    rating: driverRatingFor(strategy),
    tone: driverToneFor(strategy),
  }))
}

function normalizeValueDriver(driver, index) {
  const rating = Number(driver.rating ?? driver.score ?? driver.confidenceScore ?? 3)

  return {
    id: driver.id ?? `${driver.title ?? driver.name ?? "driver"}-${index}`,
    title: driver.title ?? driver.name ?? "Value driver",
    description: driver.description ?? driver.summary ?? "",
    rating: Math.min(Math.max(Math.round(rating), 1), 5),
    tone: driver.tone ?? (rating >= 4 ? "positive" : "warning"),
  }
}

function driverRatingFor(strategy) {
  const score = strategy.confidenceScore ?? strategy.judgementScore
  if (score == null) return 3
  if (score >= 85) return 5
  if (score >= 70) return 4
  if (score >= 50) return 3

  return 2
}

function driverToneFor(strategy) {
  return driverRatingFor(strategy) >= 4 ? "positive" : "warning"
}

function evidenceVersionFor(latestRun) {
  if (!latestRun) return null

  return latestRun.evidenceVersion ?? latestRun.evidence_version ?? latestRun.sequence ?? latestRun.id ?? null
}

function valueEnhancementsFor(strategy) {
  if (!strategy) return []

  const validationTasks = strategy.nextValidationTasks.slice(0, 4)
  const fallbackTasks = [
    "Validate market rent assumptions",
    "Confirm planning or lease constraints",
    "Price execution works with contractor input",
    "Compare hold versus exit route",
  ]

  return (validationTasks.length > 0 ? validationTasks : fallbackTasks).map((title, index) => ({
    id: `${strategy.id}-enhancement-${index + 1}`,
    title,
    impact: index === 0 ? "High" : "Medium",
    cost: index === 2 ? "Medium" : "Low",
    complexity: strategy.metrics?.complexity ?? "Medium",
    confidence: strategy.confidence ?? "Medium Confidence",
    valueUplift: index === 0 ? "High" : "Medium",
    whyItMatters: "This investigation improves confidence before appraisal.",
  }))
}

function nextValidationTasksFor(strategy) {
  const missing = arrayValue(strategy.missingEvidence).map((item) => `Capture ${formatLabel(item)}`)
  const risks = arrayValue(strategy.risks).map((item) => `Validate ${item}`)

  return [...missing, ...risks].slice(0, 5)
}

function rejectedReasonFor(strategy) {
  const risk = firstPresent(strategy.risks)
  if (risk) return risk

  return `${strategy.name} is lower ranked against current evidence.`
}

function confidenceLabel(score) {
  if (score == null) return "Medium"
  if (score >= 75) return "High"
  if (score >= 55) return "Medium"

  return "Low"
}

function compactMetricLabel(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (normalized.startsWith("high")) return "High"
  if (normalized.startsWith("strong")) return "Strong"
  if (normalized.startsWith("moderate")) return "Moderate"
  if (normalized.startsWith("medium")) return "Medium"
  if (normalized.startsWith("light")) return "Light"
  if (normalized.startsWith("low")) return "Low"
  if (normalized.length > 20) return "Medium"

  return value ? formatLabel(value) : "N/A"
}

function compactPotentialLabel(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized) return "N/A"
  if (normalized.includes("high")) return "High"
  if (normalized.includes("strong")) return "High"
  if (normalized.includes("moderate")) return "Moderate"
  if (normalized.includes("medium")) return "Medium"
  if (normalized.includes("low")) return "Low"

  return compactMetricLabel(value)
}

function compactIncomeLabel(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized) return "N/A"
  if (normalized.includes("strong")) return "Strong"
  if (normalized.includes("meaningful")) return "Meaningful"
  if (normalized.includes("moderate")) return "Moderate"
  if (normalized.includes("medium")) return "Medium"
  if (normalized.includes("light")) return "Light"
  if (normalized.includes("low")) return "Low"

  return compactMetricLabel(value)
}

function compactTimelineLabel(value) {
  const stringValue = String(value ?? "").trim()
  if (!stringValue) return "N/A"

  const range = stringValue.match(/(\d+)\s*[–-]\s*(\d+)\s*(weeks?|months?)/i)
  if (range) return `${range[1]}-${range[2]} ${formatTimelineUnit(range[3])}`

  const single = stringValue.match(/(\d+)\s*(weeks?|months?)/i)
  if (single) return `${single[1]} ${formatTimelineUnit(single[2])}`

  return compactMetricLabel(value)
}

function formatTimelineUnit(value) {
  return value.toLowerCase().startsWith("month") ? "Months" : "Weeks"
}

function normalizeWorksType(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized) return "N/A"
  if (normalized === "n/a") return "N/A"
  if (normalized.includes("paper") || normalized.includes("lease") || normalized.includes("legal")) return "Paper based"
  if (normalized.includes("light") || normalized.includes("cosmetic") || normalized.includes("furnish")) return "Light cosmetic"
  if (normalized.includes("heavy") || normalized.includes("full refurb") || normalized.includes("structural")) return "Heavy refurb"
  if (normalized.includes("extension") || normalized.includes("reconfiguration") || normalized.includes("reconfigure")) {
    return "Extension/reconfiguration"
  }
  if (normalized.includes("conversion") || normalized.includes("convert") || normalized.includes("change of use")) return "Conversion"
  if (normalized.includes("new build") || normalized.includes("ground")) return "New build"

  return "N/A"
}

function normalizePlanningNotes(value) {
  const normalized = String(value ?? "").trim()
  const lowerValue = normalized.toLowerCase()
  if (!normalized || lowerValue === "n/a") return "N/A"
  if (lowerValue.includes("permitted development")) return "Permitted development"
  if (lowerValue.includes("already granted") || lowerValue.includes("consent granted")) return "Planning already granted"
  if (lowerValue.includes("planning required") || lowerValue.includes("sui generis") || lowerValue.includes("change of use")) {
    return "Planning required"
  }

  return normalized
}

function formatPercent(value) {
  if (value == null || value === "") return "Pending"
  if (typeof value === "number") return `${value}%`

  const stringValue = String(value).trim()
  const numericValue = Number(stringValue.replace("%", ""))
  if (Number.isFinite(numericValue)) return `${Math.round(numericValue)}%`

  return stringValue
}

function scoreAverage(values) {
  const numericValues = values.filter((value) => typeof value === "number")
  if (numericValues.length === 0) return null

  return Math.round(numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length)
}

function arrayValue(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function firstPresent(value) {
  return arrayValue(value).find(Boolean)
}

function formatLabel(value) {
  const words = String(value ?? "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .trim()

  return words.replace(/\w\S*/g, (word) => (
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ))
}
